// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionTitle from "@werner/App/Components/Common/SectionTitle";
import Notify from "@werner/App/Components/Notify";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = xIsAuthenticated(state);
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated,
        widgetPageLink: getPageLinkByNodeId(state, ownProps.fields.sectionTitleUrl.value),
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

// XNG-Changes: add numberOfRecords & title & quoteStatuses
const enum fields {
    title = "title",
    sectionTitleUrl = "sectionTitleUrl",
    sectionTitleText = "sectionTitleText",
    numberOfRecords = "numberOfRecords",
    quoteStatuses = "quoteStatuses",
    showTopBorder = "showTopBorder",
}

interface QuoteStatus {
    fields: {
        status: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.title]: string;
        [fields.sectionTitleUrl]: LinkFieldValue;
        [fields.sectionTitleText]: string;
        [fields.quoteStatuses]: QuoteStatus[];
        [fields.showTopBorder]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    // XNG-Changes
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    tableWrapper?: GridItemProps;
    wrapper?: InjectableCss;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    tableWrapper: {
        css: css`
            overflow: auto;
        `,
    },
    noQuotesGridItem: { width: 12 },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
    statuses: [],
    sortBy: "OrderDate DESC",
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            // XNG-Changes
            recentQuotesParameter.pageSize = this.props.fields.numberOfRecords || recentQuotesParameter.pageSize;
            recentQuotesParameter.statuses = this.props.fields.quoteStatuses?.map(item => item.fields.status) as any;
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const {
            settingsCollection,
            session,
            quotesDataView,
            myQuotesPageNavLink,
            canViewQuotes,
            fields,
            widgetPageLink,
        } = this.props;
        if (!canViewQuotes || !quotesDataView.value || quotesDataView.value.length === 0) {
            return null;
        }

        const link = widgetPageLink?.url;
        // XNG-changes: Change Structure
        return (
            <StyledWrapper {...styles.wrapper}>
                <SectionTitle
                    title={this.props.fields.title}
                    viewMoreLink={link}
                    viewMoreText={fields.sectionTitleText}
                    border={this.props.fields.showTopBorder ? "top" : "none"}
                    data-test-selector="cardListRecentQuotes"
                />
                {quotesDataView.value.length === 0 && (
                    <Notify type="warning" {...styles.noQuotesText}>
                        {siteMessage("Rfq_NoQuotesMessage")}
                    </Notify>
                )}
                {quotesDataView.value.length > 0 && (
                    <GridItem {...styles.tableWrapper} width={12}>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Quote #")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Status")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Requested")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("PO #")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Expires")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("User")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Ship-To")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {quotesDataView.value.map(
                                    quote =>
                                        quote !== null && (
                                            <DataTableRow
                                                {...styles.dataTableRow}
                                                data-test-selector="recentQuotes_dataLine"
                                                key={quote.id}
                                            >
                                                <QuoteSummaryCard
                                                    quote={quote}
                                                    session={session}
                                                    quoteSettings={settingsCollection.quoteSettings}
                                                    extendedStyles={styles.quoteSummaryCard}
                                                />
                                            </DataTableRow>
                                        ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </GridItem>
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Recent Quotes",
                fieldType: "Translatable",
                sortOrder: 1,
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 2,
                isRequired: true,
            },
            // XNG-Changes:
            {
                name: fields.sectionTitleUrl,
                displayName: "Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Translatable",
                sortOrder: 3,
                isRequired: true,
            },
            {
                name: fields.sectionTitleText,
                displayName: "Link Title",
                editorTemplate: "TextField",
                defaultValue: "View all",
                fieldType: "Translatable",
                sortOrder: 4,
            },
            {
                name: fields.quoteStatuses,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.status.split(/(?=[A-Z])/).join(" ");
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "status",
                        editorTemplate: "SystemListDropDownField",
                        systemListName: "Werner-QuoteStatuses",
                        defaultValue: "",
                    },
                ],
                sortOrder: 5,
            },
            {
                name: fields.showTopBorder,
                displayName: "Show Top Border ",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
